import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import logoNaklejki3d from "@/assets/nowe-logo-naklejki-3d.png.asset.json";

interface StickerPreviewProps {
  width: number;
  height: number;
  shape: string;
  /** CSS background of the selected foil (color or gradient). */
  background?: string;
  borderColor?: string;
  foilVideoUrl?: string;
  foilImageUrl?: string;
  foilLabel?: string;
}

const MAX_BOX = 260; // px

/** Schematic, live-updating drawing of the sticker – changes with dimensions and shape. */
const StickerPreview = ({
  width,
  height,
  shape,
  background,
  borderColor,
  foilVideoUrl,
  foilImageUrl,
  foilLabel,
}: StickerPreviewProps) => {
  const { t } = useTranslation();
  const [videoFailed, setVideoFailed] = useState(false);
  const valid = width > 0 && height > 0;
  const ratio = valid ? width / height : 1;
  const boxW = valid ? (ratio >= 1 ? MAX_BOX : MAX_BOX * ratio) : MAX_BOX;
  const boxH = valid ? (ratio >= 1 ? MAX_BOX / ratio : MAX_BOX) : MAX_BOX;

  const radius = shape === "round" ? "50%" : shape === "custom" ? "18px" : "6px";

  useEffect(() => {
    setVideoFailed(false);
  }, [foilVideoUrl]);

  const showVideo = Boolean(foilVideoUrl && !videoFailed);
  const showImage = Boolean(foilImageUrl && !showVideo);

  return (
    <div className="bg-navy-light border border-primary-foreground/10 rounded-2xl p-5 lg:p-7 mb-4">
      <p className="text-primary-foreground/60 text-xs font-bold tracking-widest uppercase mb-2">
        {t("calc.previewTitle")}
      </p>
      <p className="text-primary-foreground/50 text-xs mb-4">
        {t("calc.previewDesc")}
      </p>
      <div className="flex items-center justify-center min-h-[280px]">
        {valid ? (
          <figure className="text-center">
            <div
              className="relative mx-auto transition-all duration-300 shadow-[0_10px_30px_rgba(0,0,0,0.35)] overflow-hidden"
              style={{
                width: `${boxW}px`,
                height: `${boxH}px`,
                borderRadius: radius,
                background: background && background !== "transparent" ? background : "rgba(255,255,255,0.12)",
                border: `2px solid ${borderColor || "rgba(255,255,255,0.35)"}`,
                backgroundImage:
                  background && background !== "transparent"
                    ? undefined
                    : "linear-gradient(45deg, rgba(255,255,255,0.14) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.14) 50%, rgba(255,255,255,0.14) 75%, transparent 75%)",
                backgroundSize: background && background !== "transparent" ? undefined : "14px 14px",
              }}
              role="img"
              aria-label={t("calc.previewCaption", { width, height })}
            >
              {showVideo && (
                <video
                  key={foilVideoUrl}
                  src={foilVideoUrl}
                  poster={foilImageUrl}
                  className="absolute inset-0 h-full w-full object-cover"
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="metadata"
                  aria-label={foilLabel}
                  onError={() => setVideoFailed(true)}
                />
              )}
              {showImage && (
                <img
                  src={foilImageUrl}
                  alt=""
                  className="absolute inset-0 h-full w-full object-cover"
                  aria-hidden="true"
                />
              )}
              {(showVideo || showImage) && (
                <span className="absolute inset-0 bg-primary-foreground/5 pointer-events-none" aria-hidden="true" />
              )}
              <img
                src={logoNaklejki3d.url}
                alt=""
                className={`absolute inset-0 m-auto w-[55%] h-[55%] object-contain pointer-events-none select-none drop-shadow-lg ${showVideo || showImage ? "opacity-55" : "opacity-20"}`}
                aria-hidden="true"
              />
            </div>
            <figcaption className="text-primary-foreground/50 text-xs mt-4">
              {t("calc.previewCaption", { width, height })}
            </figcaption>
          </figure>
        ) : (
          <p className="text-primary-foreground/40 text-sm text-center">
            {t("calc.fillDimensions")}
          </p>
        )}
      </div>
    </div>
  );
};

export default StickerPreview;
